type DateFormatType = 'short' | 'medium' | 'long' | 'full';

const dateFormats = {
    en_us: {
        short: 'MM/dd/yyyy',
        medium: 'MMM dd, yyyy',
        long: 'MMMM dd, yyyy',
        full: 'EEEE, MMMM dd, yyyy',
    },
    non_us: {
        short: 'dd/MM/yyyy',
        medium: 'dd MMM yyyy',
        long: 'dd MMMM yyyy',
        full: 'EEEE, dd MMMM yyyy',
    },
};
const getLocalAndFormat = (formatType: DateFormatType) => {
    const locale = getIntlDateFormatLocale();
    const localeFormat = locale === 'en-US' ? dateFormats.en_us : dateFormats.non_us;
    let dateFormat = '';
    switch (formatType) {
        case 'medium':
            dateFormat = localeFormat.medium;
            break;
        case 'long':
            dateFormat = localeFormat.long;
            break;
        case 'full':
            dateFormat = localeFormat.full;
            break;
        default:
            dateFormat = localeFormat.short;
    }
    return {
        locale,
        dateFormat,
    };
};

export const customDateFormat = (formatType: DateFormatType = 'short') => {
    //objective if browser locale is en-US use DD/MM/YYYY else MM/DD/YYYY
    //format date picker provider configuration
    const { locale, dateFormat } = getLocalAndFormat(formatType);
    return locale === 'en-US'
        ? {
              parse: {
                  dateInput: dateFormat.toUpperCase(),
              },
              display: {
                  dateInput: dateFormat.toUpperCase(),
                  monthYearLabel: 'MMM YYYY',
                  dateA11yLabel: 'LL',
                  monthYearA11yLabel: 'MMMM YYYY',
              },
          }
        : {
              parse: {
                  dateInput: dateFormat.toUpperCase(),
              },
              display: {
                  dateInput: dateFormat.toUpperCase(),
                  monthYearLabel: 'MM YYYY',
                  dateA11yLabel: 'DD/MM/YYYY',
                  monthYearA11yLabel: 'MM YYYY',
              },
          };
};

export const getIntlDateFormatLocale = () => {
    return Intl.DateTimeFormat().resolvedOptions().locale;
};

export const getDateFormatHint = (formatType: DateFormatType = 'short') => {
    return getDateFormat(formatType);
};

export const getDateFormat = (format: DateFormatType = 'short') => {
    const { dateFormat } = getLocalAndFormat(format);
    return dateFormat;
};

export const getTimeZoneOffset = () => {
    return new Date().getTimezoneOffset();
};

export const getDateCompare = (
    date1: string | Date | null | undefined,
    date2: string | Date | null | undefined,
): number => {
    const includedValues = ['---'];
    const includedDate1 = typeof date1 === 'string' && includedValues.includes(date1);
    const includedDate2 = typeof date2 === 'string' && includedValues.includes(date2);

    if ((!date1 && !date2) || (includedDate1 && includedDate2)) return 0;
    if (!date1 || includedDate1) return -1;
    if (!date2 || includedDate2) return 1;

    // Convert strings to Date objects if needed
    const d1 = date1 instanceof Date ? date1 : new Date(date1);
    const d2 = date2 instanceof Date ? date2 : new Date(date2);

    // Compare timestamps
    return d1.getTime() - d2.getTime();
};

const dateTimeFormat = (
    locale: string | undefined = undefined,
    options?: Intl.DateTimeFormatOptions,
) => new Intl.DateTimeFormat(locale, options);

const relativeTimeFormat = (
    locale: string | undefined = undefined,
    options?: Intl.RelativeTimeFormatOptions,
) => new Intl.RelativeTimeFormat(locale, options);

const formatDate = (
    date: Date,
    format = 'MM/DD/YYYY',
    locale: string | undefined = undefined,
) => {
    let options = {};

    switch (format) {
        case 'MM/DD/YYYY':
            options = {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
            };
            break;
        case 'YYYY/MM/DD':
            options = {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            };
            break;
        case 'DD/MM/YYYY':
            options = {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            };
            break;
        case 'MMMM Do, YYYY':
            options = {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
            };
            break;
        case 'MMM D, YYYY':
            options = {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            };
            break;
        default:
            return date.toLocaleDateString(locale);
    }

    return dateTimeFormat(locale, options).format(date);
};

const formatDateTime = (
    date: Date,
    locale: string | undefined = undefined,
    options: Partial<Intl.DateTimeFormatOptions> = {},
) => {
    const defaultOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    };

    return new Intl.DateTimeFormat(locale, { ...defaultOptions, ...options }).format(
        date,
    );
};

const timeAgo = (date: Date, locale: string | undefined = undefined) => {
    const now = new Date();
    const _date = new Date(date);
    const difInSeconds = Math.floor((now.getTime() - _date.getTime()) / 1000);

    const intervals = {
        year: 31536000,
        month: 2592000,
        week: 604800,
        day: 86400,
        hour: 3600,
        minute: 60,
        second: 1,
    };

    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
        const diff = Math.floor(difInSeconds / secondsInUnit);

        if (diff >= 1) {
            return relativeTimeFormat(locale, {
                numeric: 'auto',
            }).format(-diff, unit as Intl.RelativeTimeFormatUnit);
        }
    }

    return relativeTimeFormat(locale, {
        numeric: 'auto',
    }).format(0, 'second');
};

export { formatDate, formatDateTime, timeAgo };
